/**
 * Suki V2 — moduł zarządzania sukami hodowlanymi (UI 2.0).
 * AKTUALNE. Zastępuje legacy ProfilePanel?tab=dogs.
 *
 * Dane: GET /api/kennels/{kennelId}/dogs (filtr: samice, nie-reproduktory).
 * Dodaj/Edytuj → DogWizard. Usuń → DELETE /api/dogs/{id}. Podgląd → /panel/pies/{id}.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PawPrint } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ModuleListV2, ModuleCardV2, ConfirmDeleteDialog, useModuleDelete } from './ModuleListV2';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SukiV2() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [kennelId, setKennelId] = useState(null);
  const [items, setItems] = useState([]);
  const redirectedRef = useRef(false);
  const [showKennelPrompt, setShowKennelPrompt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const kRes = await axios.get(`${API}/kennels`, { headers });
      const k = (kRes.data || [])[0];
      if (!k) { setItems([]); setKennelId(null); return; }
      setKennelId(k.id);
      const dRes = await axios.get(`${API}/kennels/${k.id}/dogs`, { headers });
      const suki = (dRes.data || []).filter(
        (d) => d.gender === 'female' && d.listing_type !== 'stud' && !d.is_stud
      );
      setItems(suki);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Masz hodowlę + brak suk → od razu kreator suki (zgodnie z „od razu kreator").
  // Brak hodowli → popup: „Żeby dodać sukę, najpierw uzupełnij stronę hodowli" (bez cichego skoku).
  useEffect(() => {
    if (loading || redirectedRef.current) return;
    if (items.length === 0 && kennelId) {
      redirectedRef.current = true;
      navigate(`/wizard/pies/${kennelId}?typ=suka`, { replace: true });
    } else if (!kennelId) {
      setShowKennelPrompt(true);
    }
  }, [loading, kennelId, items, navigate]);

  const del = useModuleDelete(
    async (dog) => {
      await axios.delete(`${API}/dogs/${dog.id}`, { headers });
      toast.success('Suka usunięta');
    },
    load
  );

  const handleAdd = () => {
    if (!kennelId) { toast('Najpierw utwórz stronę hodowli — potem dodasz suki.', { icon: '💡' }); navigate('/wizard/hodowla-v2'); return; }
    navigate(`/wizard/pies/${kennelId}?typ=suka`);
  };

  return (
    <>
      <ModuleListV2
        activeKey="dogs"
        contextLabel="Suki"
        icon={PawPrint}
        title="Suki"
        subtitle="Suki hodowlane w Twojej hodowli"
        addLabel={kennelId ? 'Dodaj sukę' : 'Utwórz stronę hodowli'}
        onAdd={handleAdd}
        loading={loading || (items.length === 0 && !!kennelId)}
        items={items}
        empty={kennelId ? {
          icon: PawPrint,
          title: 'Nie masz jeszcze żadnej suki',
          text: 'Dodaj pierwszą sukę hodowlaną — to fundament Twojej hodowli i podstawa pod planowanie miotów.',
        } : {
          icon: PawPrint,
          title: 'Najpierw utwórz stronę hodowli',
          text: 'Suki dodajesz w ramach swojej hodowli. Utwórz stronę hodowli (zajmie chwilę), a potem dodasz tu swoje suki hodowlane.',
        }}
      >
        {items.map((d) => (
          <ModuleCardV2
            key={d.id}
            id={d.id}
            image={d.main_photo}
            title={d.name}
            subtitle={d.breed}
            badges={[
              { label: 'Suka', className: 'bg-pink-50 text-pink-600' },
              ...(d.titles ? [{ label: d.titles, className: 'bg-amber-50 text-amber-700' }] : []),
            ]}
            onEdit={() => navigate(`/panel/pies/${d.id}/edytuj`)}
            onPreview={() => navigate(`/panel/pies/${d.id}/edytuj`)}
            onDelete={() => del.setTarget(d)}
          />
        ))}
      </ModuleListV2>
      <ConfirmDeleteDialog {...del.dialogProps} />

      <AlertDialog open={showKennelPrompt} onOpenChange={setShowKennelPrompt}>
        <AlertDialogContent data-testid="suki-kennel-required-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Najpierw uzupełnij stronę hodowli</AlertDialogTitle>
            <AlertDialogDescription>
              Żeby dodać sukę, musisz mieć uzupełnioną stronę hodowli. Suki są częścią Twojej hodowli i prezentują się na jej stronie.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="suki-kennel-required-cancel">Później</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigate('/wizard/hodowla-v2')}
              data-testid="suki-kennel-required-create"
              className="bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)]"
            >
              Utwórz stronę hodowli
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
